'use client';

import React from 'react';
import {
  Search, Loader2, Building2, Package, ChevronDown,
  ClipboardList, AlertTriangle,
} from 'lucide-react';
import type {
  Supplier,
  PurchaseOrder,
  PoFulfillment,
} from './useProcurementSearch';

interface SupplierPOSelectorProps {
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (s: Supplier | null) => void;
  supplierSearchQuery: string;
  setSupplierSearchQuery: (q: string) => void;
  showSupplierSearch: boolean;
  setShowSupplierSearch: (show: boolean) => void;
  filteredSuppliers: Supplier[];
  loadingSuppliers: boolean;
  selectedPurchaseOrder: PurchaseOrder | null;
  setSelectedPurchaseOrder: (po: PurchaseOrder | null) => void;
  poSearchQuery: string;
  setPoSearchQuery: (q: string) => void;
  poSearchResults: PurchaseOrder[];
  isSearchingPO: boolean;
  showPOSearch: boolean;
  setShowPOSearch: (show: boolean) => void;
  procurementSetupExpanded: boolean;
  setProcurementSetupExpanded: (expanded: boolean) => void;
  requiresPurchaseOrder: boolean;
  poLineItems: Array<{ description: string; quantity: number; quantity_received: number }>;
  poFulfillment: PoFulfillment | null;
  supplierDropdownRef: React.RefObject<HTMLDivElement | null>;
  poDropdownRef: React.RefObject<HTMLDivElement | null>;
  selectedEntryType: string;
  onClearPO: () => void;
}

export function SupplierPOSelector({
  selectedSupplier,
  setSelectedSupplier,
  supplierSearchQuery,
  setSupplierSearchQuery,
  showSupplierSearch,
  setShowSupplierSearch,
  filteredSuppliers,
  loadingSuppliers,
  selectedPurchaseOrder,
  setSelectedPurchaseOrder,
  poSearchQuery,
  setPoSearchQuery,
  poSearchResults,
  isSearchingPO,
  showPOSearch,
  setShowPOSearch,
  procurementSetupExpanded,
  setProcurementSetupExpanded,
  requiresPurchaseOrder,
  poLineItems,
  poFulfillment,
  supplierDropdownRef,
  poDropdownRef,
  selectedEntryType,
  onClearPO,
}: SupplierPOSelectorProps) {
  const supplierSelected = selectedSupplier && (!requiresPurchaseOrder || selectedPurchaseOrder);

  return (
    <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
      {/* Collapsed summary */}
      {supplierSelected && !procurementSetupExpanded && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 cursor-pointer"
          onClick={() => setProcurementSetupExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setProcurementSetupExpanded(true);
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Building2 className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {selectedSupplier!.company_name || selectedSupplier!.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  {selectedPurchaseOrder ? (
                    <>
                      <Package className="h-3 w-3 flex-shrink-0" />
                      {selectedPurchaseOrder!.item_number} — {selectedPurchaseOrder!.title}
                    </>
                  ) : (
                    <span className="text-gray-400">No PO linked (optional)</span>
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearPO();
                }}
                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 px-2 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Cancel
              </button>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
      )}

      {/* Expanded form */}
      {procurementSetupExpanded && (
        <div className="space-y-3">
          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Supplier *
            </label>
            {selectedSupplier ? (
              <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.company_name || selectedSupplier.name}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSupplier(null);
                    setSelectedPurchaseOrder(null);
                    setSupplierSearchQuery('');
                    setPoSearchQuery('');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative" ref={supplierDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={supplierSearchQuery}
                    onChange={(e) => {
                      setSupplierSearchQuery(e.target.value);
                      setShowSupplierSearch(true);
                    }}
                    onFocus={() => setShowSupplierSearch(true)}
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                {showSupplierSearch && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    {loadingSuppliers ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                        Loading suppliers...
                      </div>
                    ) : filteredSuppliers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {supplierSearchQuery.trim() ? 'No suppliers found' : 'No suppliers available'}
                      </div>
                    ) : (
                      <div>
                        {filteredSuppliers.map((supplier) => (
                          <button
                            key={supplier.id}
                            type="button"
                            onClick={() => {
                              setSelectedSupplier(supplier);
                              setShowSupplierSearch(false);
                              setSupplierSearchQuery('');
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {supplier.company_name || supplier.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Purchase Order (only after supplier selected) */}
          {selectedSupplier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Purchase Order{requiresPurchaseOrder ? ' *' : ' (optional)'}
              </label>
              {selectedPurchaseOrder ? (
                <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {selectedPurchaseOrder.item_number}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedPurchaseOrder.title}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPurchaseOrder(null);
                      setPoSearchQuery('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative" ref={poDropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search purchase orders..."
                      value={poSearchQuery}
                      onChange={(e) => {
                        setPoSearchQuery(e.target.value);
                        setShowPOSearch(true);
                      }}
                      onFocus={() => setShowPOSearch(true)}
                      className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  {showPOSearch && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      {isSearchingPO ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                          Searching...
                        </div>
                      ) : poSearchResults.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          {poSearchQuery.length >= 2 || !poSearchQuery.trim()
                            ? 'No purchase orders found for this supplier'
                            : 'Type 2+ characters to search'}
                        </div>
                      ) : (
                        <div>
                          {poSearchResults.map((po) => (
                            <button
                              key={po.id}
                              type="button"
                              onClick={() => {
                                setSelectedPurchaseOrder(po);
                                setShowPOSearch(false);
                                setPoSearchQuery('');
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">
                                {po.item_number}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {po.title} — {po.contact_name}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PO Line Items Reference */}
      {selectedPurchaseOrder && poLineItems.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                Purchase Order Line Items ({poLineItems.length})
              </span>
            </div>
            {poFulfillment?.isFullyDelivered && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                Fully Delivered
              </span>
            )}
            {poFulfillment?.isPartiallyDelivered && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Partially Delivered
              </span>
            )}
            {poFulfillment && !poFulfillment.isFullyDelivered && !poFulfillment.isPartiallyDelivered && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                Open
              </span>
            )}
          </div>

          {/* Fulfillment progress */}
          {poFulfillment && poFulfillment.totalOrdered > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>Delivered: {poFulfillment.totalReceived} of {poFulfillment.totalOrdered}</span>
                <span>{Math.round((poFulfillment.totalReceived / poFulfillment.totalOrdered) * 100)}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-2 rounded-full ${poFulfillment.isFullyDelivered ? 'bg-red-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(100, (poFulfillment.totalReceived / poFulfillment.totalOrdered) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Fully delivered warning for delivery notes */}
          {selectedEntryType === 'delivery_note' && poFulfillment?.isFullyDelivered && (
            <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              This purchase order is fully delivered. No further delivery notes can be applied.
            </div>
          )}

          {/* Line items table */}
          <div className="space-y-2">
            {poLineItems.map((line, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {line.description}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                  <span>Ordered: <span className="font-medium text-gray-700 dark:text-gray-300">{line.quantity}</span></span>
                  {selectedEntryType === 'delivery_note' && (
                    <span className="contents">
                      <span>Received: <span className="font-medium text-gray-700 dark:text-gray-300">{line.quantity_received}</span></span>
                      <span>Remaining: <span className={`font-medium ${Math.max(0, line.quantity - line.quantity_received) === 0 ? 'text-red-500' : 'text-blue-500'}`}>{Math.max(0, line.quantity - line.quantity_received)}</span></span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
